import React, { useEffect, useState } from "react";
import Navigation from "./Navigation";
import Footer from "./Footer";

const CONTACT_HREF =
  "mailto:marcos@onsideai.org?subject=Sentinel%20%C2%B7%20Norway%20enquiry";
const STORMBERRY_HREF = "https://stormberry.as";

const NORWAY_SECTORS = [
  "Energy",
  "Oil & Gas",
  "Offshore",
  "Maritime",
  "Ports & Cargo",
  "Aquaculture",
  "Power & Grid",
  "Pipelines",
  "Heavy Industry",
  "Logistics",
  "Critical Infrastructure",
  "Public Safety",
];

const NORWAY_CONFIG_ROWS = [
  {
    lead: "Delivered by Stormberry.",
    body: "Discovery, deployment, and tuning handled locally by Stormberry in Norway.",
  },
  {
    lead: "Works with your cameras.",
    body: "No rip-and-replace. Sentinel runs on the cameras you already own.",
  },
  {
    lead: "Cloud, on-prem, or hybrid.",
    body: "Deploy where it fits your security posture. Same product, same prompts, same alerts — wherever the workload lives.",
  },
  {
    lead: "Stays on your network.",
    body: "Encrypted end-to-end and authenticated on both ends. No public internet exposure.",
  },
];

const Norway: React.FC = () => {
  const [cfgStates, setCfgStates] = useState<boolean[]>(
    NORWAY_CONFIG_ROWS.map(() => true)
  );
  const toggleCfg = (i: number) =>
    setCfgStates((s) => s.map((v, j) => (i === j ? !v : v)));
  const cfgActive = cfgStates.filter(Boolean).length;

  useEffect(() => {
    const els = document.querySelectorAll(".sentinel-console .reveal");
    const obs = new IntersectionObserver(
      (entries, o) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("show");
            o.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="sentinel-page sentinel-console sentinel-norway">
      <nav className="home-header">
        <Navigation />
      </nav>

      {/* HERO */}
      <section className="sn-hero">
        <div className="sn-hero-bg" aria-hidden="true">
          <div className="sn-hero-grid-lines" />
          <div className="sn-hero-glow" />
        </div>

        <div className="sn-container sn-hero-grid sn-hero-grid-narrow">
          <div className="sn-hero-content reveal">
            <p className="sn-eyebrow">
              <span className="bracket">[</span> ONSIDE&nbsp;/&nbsp;NORWAY{" "}
              <span className="status-dot" /> LIVE <span className="bracket">]</span>
            </p>

            <h1>
              Sentinel <span className="ink-soft">in Norway.</span>
            </h1>

            <p className="sn-subhead">
              Real-time AI surveillance, deployed locally with our official
              distribution partner.
            </p>

            <p className="sn-lede">
              OnSide AI&rsquo;s Sentinel platform turns the cameras you already
              own into analysts that watch in real time &mdash; rules written in
              plain English, alerts in seconds. Now available in Norway through
              <strong className="sn-stormberry"> Stormberry</strong>.
            </p>

            <div className="sn-hero-ctas">
              <a className="btn-indigo" href={CONTACT_HREF}>
                Talk to us
                <span className="arrow">→</span>
              </a>
              <a
                className="btn-ghost"
                href={STORMBERRY_HREF}
                target="_blank"
                rel="noreferrer"
              >
                Visit Stormberry
                <span className="arrow">↗</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERS — Milestone */}
      <section className="sn-partners reveal">
        <div className="sn-container">
          <div className="sn-partner-strip">
            <p className="sn-partner-strip-label">
              <span className="bracket">[</span> OFFICIAL TECHNOLOGY PARTNER{" "}
              <span className="bracket">]</span>
            </p>
            <div className="sn-partner-strip-logo">
              <img src="/images/milestone-logo.png" alt="Milestone Systems" />
            </div>
          </div>
        </div>
      </section>

      {/* PARTNER CARD — Stormberry (local distribution) */}
      <section className="sn-partner reveal">
        <div className="sn-container narrow">
          <p className="sn-eyebrow">
            <span className="bracket">[</span> OFFICIAL DISTRIBUTION PARTNER{" "}
            <span className="bracket">]</span>
          </p>

          <div className="sn-partner-card">
            <div className="sn-partner-card-head">
              <div className="sn-partner-flag" aria-hidden="true">
                <svg
                  viewBox="0 0 22 16"
                  xmlns="http://www.w3.org/2000/svg"
                  width="100%"
                  height="100%"
                >
                  <rect width="22" height="16" fill="#BA0C2F" />
                  <rect x="6" y="0" width="4" height="16" fill="#FFFFFF" />
                  <rect x="0" y="6" width="22" height="4" fill="#FFFFFF" />
                  <rect x="7" y="0" width="2" height="16" fill="#00205B" />
                  <rect x="0" y="7" width="22" height="2" fill="#00205B" />
                </svg>
              </div>
              <div className="sn-partner-titleblock">
                <p className="sn-partner-kicker">NORWAY · NORGE</p>
                <h2>Stormberry</h2>
                <p className="sn-partner-role">
                  Official OnSide AI distribution partner for Norway
                </p>
              </div>
            </div>

            <div className="sn-partner-body">
              <p>
                Sentinel is delivered, deployed, and supported in Norway by
                Stormberry &mdash; bringing the local relationships, language,
                and operational knowledge to roll Sentinel out across Norwegian
                sites, from offshore platforms to critical infrastructure on
                the mainland.
              </p>
            </div>

            <div className="sn-partner-meta">
              <div className="meta-row">
                <span className="meta-key">Partnership</span>
                <span className="meta-val">Official distributor · Norway</span>
              </div>
              <div className="meta-row">
                <span className="meta-key">Web</span>
                <span className="meta-val">
                  <a href={STORMBERRY_HREF} target="_blank" rel="noreferrer">
                    stormberry.as ↗
                  </a>
                </span>
              </div>
              <div className="meta-row">
                <span className="meta-key">Norway enquiries</span>
                <span className="meta-val">
                  <a href={CONTACT_HREF}>marcos@onsideai.org</a>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WALKTHROUGH — same as home page, with Norway-relevant prompt example */}
      <section id="how-it-works" className="sn-walk">
        <div className="sn-container">
          <div className="sn-walk-head reveal">
            <p className="sn-eyebrow">
              <span className="bracket">[</span> HOW IT WORKS{" "}
              <span className="bracket">]</span>
            </p>
            <h2>From sentence to signal.</h2>
            <p className="sn-section-sub">
              Three steps. You write the first. The next two happen without
              you.
            </p>
          </div>

          <ol className="sn-steps">
            <li className="sn-step reveal">
              <div className="sn-step-meta">
                <span className="sn-step-num">01</span>
                <span className="sn-step-rule" />
                <span className="sn-step-tag">PROMPT</span>
              </div>
              <div className="sn-step-grid">
                <div className="sn-step-text">
                  <h3>Type a rule.</h3>
                  <p>
                    Operators type in plain English. No models to train, no
                    code to write, no data scientists to hire.
                  </p>
                  <div className="sn-prompt-card">
                    <span className="sn-prompt-arrow">&gt;</span>
                    <span className="sn-prompt-text">
                      tell me if a vessel approaches within 500m of the platform
                    </span>
                    <span className="sn-prompt-cursor" />
                  </div>
                  <p className="sn-step-foot">
                    Sentinel expands the rule, picks the cameras, and starts
                    watching the next frame.
                  </p>
                </div>
                <figure className="sn-step-frame sn-step-frame-32">
                  <div className="sn-step-frame-chrome">
                    <span className="chrome-title">
                      configuration · queries
                    </span>
                  </div>
                  <img
                    src="/images/sentinel-step-01-prompt.png"
                    alt="Operator typing a plain-English detection rule into Sentinel's configuration panel"
                  />
                </figure>
              </div>
            </li>

            <li className="sn-step sn-step-peak reveal">
              <div className="sn-step-meta">
                <span className="sn-step-num">02</span>
                <span className="sn-step-rule" />
                <span className="sn-step-tag">WATCH</span>
              </div>
              <div className="sn-step-grid sn-step-grid-stacked">
                <div className="sn-step-text">
                  <h3>Watch every camera.</h3>
                  <p>
                    Sentinel scores every frame from every assigned camera
                    against your rule, in real time. Hundreds of rules can run
                    in parallel.
                  </p>
                  <div className="sn-status-row">
                    <span className="sn-status-pill">
                      <span className="status-dot" /> 3 of 16 ONLINE
                    </span>
                    <span className="sn-status-pill alert">
                      <span className="status-dot red" /> 6 ALERTS
                    </span>
                    <span className="sn-status-pill mute">24 FPS</span>
                  </div>
                </div>
                <figure className="sn-step-frame sn-step-frame-169 sn-step-frame-wide">
                  <div className="sn-step-frame-chrome">
                    <span className="chrome-spacer" />
                    <span className="chrome-status">
                      <span className="status-dot" /> LIVE
                    </span>
                  </div>
                  <img
                    src="/images/sentinel-step-02-monitor.png"
                    alt="Sentinel's live operator dashboard showing camera feeds and real-time alerts"
                  />
                </figure>
              </div>
            </li>

            <li className="sn-step sn-step-rev reveal">
              <div className="sn-step-meta">
                <span className="sn-step-num">03</span>
                <span className="sn-step-rule" />
                <span className="sn-step-tag">ALERT</span>
              </div>
              <div className="sn-step-grid">
                <figure className="sn-step-frame sn-step-frame-32">
                  <div className="sn-step-frame-chrome">
                    <span className="chrome-title">
                      incident · evidence frame
                    </span>
                    <span className="chrome-conf">85% conf</span>
                  </div>
                  <img
                    src="/images/sentinel-step-03-alert.png"
                    alt="Sentinel alert detail showing the matched evidence frame and the rule that fired"
                  />
                </figure>
                <div className="sn-step-text">
                  <h3>Get the evidence.</h3>
                  <p>
                    When a rule fires you get the frame, the observation, the
                    confidence, and the timestamp. Decide once. Move on.
                  </p>
                  <div className="sn-evidence-strip">
                    <span className="ev-key">Rule</span>
                    <span className="ev-val">
                      tell me if someone falls down on the ground
                    </span>
                  </div>
                  <div className="sn-evidence-strip">
                    <span className="ev-key">Confidence</span>
                    <span className="ev-val">85%</span>
                  </div>
                </div>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* SECTORS RELEVANT TO NORWAY */}
      <section className="sn-sectors reveal">
        <div className="sn-container">
          <p className="sn-eyebrow">
            <span className="bracket">[</span> WHERE IT FITS IN NORWAY{" "}
            <span className="bracket">]</span>
          </p>
          <h2>Built for the sites that matter.</h2>

          <div className="sn-tag-grid">
            {NORWAY_SECTORS.map((s) => (
              <span className="sn-tag" key={s}>
                <span className="sn-tag-dot" />
                {s}
              </span>
            ))}
          </div>

          <p className="sn-sectors-closer">
            Hundreds of pre-built prompts. Any sentence you can write becomes a
            sensor &mdash; tuned for your site by Stormberry.
          </p>
        </div>
      </section>

      {/* DEPLOYMENT — abbreviated config panel */}
      <section className="sn-terms reveal">
        <div className="sn-container narrow">
          <p className="sn-eyebrow">
            <span className="bracket">[</span> DEPLOYMENT{" "}
            <span className="bracket">]</span>
          </p>
          <h2>Local rollout. Local support.</h2>

          <div className="sn-config-panel">
            <div className="sn-config-header">
              <span className="sn-config-title">
                <span className="status-dot" /> SENTINEL · NORWAY ROLLOUT
              </span>
              <span className="sn-config-meta">
                {cfgActive} / {NORWAY_CONFIG_ROWS.length} ACTIVE
              </span>
            </div>

            <ol className="sn-config-list">
              {NORWAY_CONFIG_ROWS.map((row, i) => {
                const on = cfgStates[i];
                return (
                  <li key={row.lead} className={on ? undefined : "is-off"}>
                    <span className="cfg-num">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="cfg-text">
                      <h4>{row.lead}</h4>
                      <p>{row.body}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={`${on ? "Disable" : "Enable"}: ${row.lead}`}
                      className={`cfg-toggle${on ? " on" : " off"}`}
                      onClick={() => toggleCfg(i)}
                    />
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="sn-final reveal">
        <div className="sn-container">
          <h2>See it on your cameras.</h2>
          <p className="sn-final-sub">
            <em>Real footage. Real prompts. Real alerts.</em>
          </p>
          <p className="sn-final-body">
            Schedule a discovery call &mdash; we&rsquo;ll show you Sentinel
            running on a sector that mirrors yours, with Stormberry leading
            the rollout in Norway.
          </p>
          <a className="btn-indigo btn-large" href={CONTACT_HREF}>
            Book a discovery call
            <span className="arrow">→</span>
          </a>
          <p className="sn-final-mark">
            <span className="line">OnSide AI Inc. &middot; Stormberry</span>
            <span className="line">Think it. Prompt it. Protected.</span>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Norway;
